<script setup lang="ts">
const props = defineProps({
  casinoGameId: {
    type: Number,
    required: true,
  },
});

enum VotesType {
  disliked = 'disliked',
  liked = 'liked',
  unvoted = 'unvoted',
}

interface VoteCount {
  is_like: VotesType;
  total_votes: number;
}

interface UserVote {
  casino_game_id: number;
  game_name: string;
  is_like: VotesType;
  user_id: number;
}

const { ApiService } = useApiService();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin } = useAuth();

const isLoading = ref<boolean>(false);
const userVote = ref<UserVote | null>(null);
const likedVotesCount = ref<string>('0');
const dislikedVotesCount = ref<string>('0');
const totalVotesCount = ref<number>(0);

const buttonClassName = computed(() => userVote.value?.is_like);

async function getVotes(gameId: number): Promise<void> {
  if (!isAuthenticated.value) {
    return;
  }

  isLoading.value = true;

  try {
    const { data } = await ApiService.get<UserVote>(
      `/casino-game-votes?casinoGameId=${gameId}`,
    );

    userVote.value = data;
  } catch (error) {
    userVote.value = null;
  } finally {
    isLoading.value = false;
  }
}

async function getVotesCount(gameId: number): Promise<void> {
  isLoading.value = true;

  try {
    const { data } = await ApiService.get<VoteCount[]>(
      `/casino-game-votes/count/${gameId}`,
    );

    data.forEach((entry) => {
      if (entry.is_like === 'liked') {
        likedVotesCount.value = String(entry.total_votes);
      }

      if (entry.is_like === 'disliked') {
        dislikedVotesCount.value = String(entry.total_votes);
      }
    });

    totalVotesCount.value = data.reduce(
      (acc, curr) => acc + curr.total_votes,
      0,
    );
  } catch (error) {
    totalVotesCount.value = 0;
  } finally {
    isLoading.value = false;
  }
}

async function submitVote(gameId: number, vote: VotesType): Promise<void> {
  isLoading.value = true;

  try {
    await ApiService.post<boolean>(`/casino-game-votes/store/`, {
      casinoGameId: gameId,
      is_like: vote,
    });
  } finally {
    isLoading.value = false;
  }
}

async function deleteVote(gameId: number): Promise<void> {
  try {
    isLoading.value = true;

    await ApiService.delete<boolean>(`/casino-game-votes/destroy/${gameId}`);
  } finally {
    isLoading.value = false;
  }
}

async function updateVotesAndCount(): Promise<void> {
  await Promise.all([
    getVotesCount(props.casinoGameId),
    getVotes(props.casinoGameId),
  ]);
}

async function handleVote(vote: VotesType): Promise<void> {
  if (!hasPermissionsToVote.value) {
    return openLoginModalToVote();
  }

  await deleteVote(props.casinoGameId);

  if (userVote.value?.is_like !== vote) {
    await submitVote(props.casinoGameId, vote);
  }

  updateVotesAndCount();
}

const hasPermissionsToVote = computed<boolean>(() => {
  return isAuthenticated.value;
});

function openLoginModalToVote(): void {
  openLogin();
}

onMounted(() => {
  updateVotesAndCount();
});
</script>

<template>
  <div class="flex flex-row items-center justify-start">
    <button
      class="text-lg text-texts bg-transparent md:hover:opacity-80 casino-votes__up flex items-center justify-center gap-1.5 md:gap-2.5 border border-solid border-white/10 p-[10px] md:p-[14px] h-[28px] md:h-[36px] rounded-md"
      :class="{
        'bg-white/10': buttonClassName === 'liked',
      }"
      @click="handleVote(VotesType.liked)"
    >
      <NuxtIcon
        class="text-[12px] opacity-60 mb-0.5"
        :class="{
          'text-white !opacity-100': buttonClassName === 'liked',
        }"
        name="thumbs_up"
      />
      <small
        v-if="likedVotesCount"
        class="text-texts text-[10px] md:text-[13px] font-medium"
      >
        {{ totalVotesCount }}
      </small>
    </button>
  </div>
</template>
